// /chord-craft/palette — the "toppings" explorer. Pick a root, a base
// triad/sus, a seventh, and a handful of extensions; a big serif chord
// symbol updates live and CraftPiano lights up the chord with skeleton
// tones (root/3rd/5th/7th) in the 'right' role and color tones (the
// extensions) glowing in the 'color' role — literally *see* the color you
// just added. "hear it" / "strum it" / "plain vs colored" let you listen
// the same way. The PALETTE gallery on the right is a set of named colors
// (mood + cross-genre "heard in") you can drop onto whatever root you're
// on; genre never gates a color — see the footnote.
//
// Theory + naming: src/lib/chordCraft.js (Agent A). Chrome + piano:
// src/components/chordCraft/CraftShell.js, CraftPiano.js (Agent B).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CraftShell from "../components/chordCraft/CraftShell";
import CraftPiano from "../components/chordCraft/CraftPiano";
import {
  BASES,
  SEVENTHS,
  EXTENSIONS,
  chordSymbol,
  voiceChord,
  specFromPreset,
  PALETTE,
} from "../lib/chordCraft";
import { NOTE_NAMES_SHARP, NOTE_NAMES_FLAT } from "../lib/pianoDrills";
import { PATCHES, playNote } from "../lib/homeKeyAudio";
import { useMidiInput } from "../components/synthlab/useSynthInput";
import { cn } from "../lib/utils";

/* ---------- persisted setup (plain JSON, safe parse) ---------- */

const STORAGE_KEY = "chordCraft:palette:v1";

const DEFAULTS = { rootPc: 0, baseId: "maj", seventhId: null, extensions: [], flats: false };

const loadState = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const p = raw ? JSON.parse(raw) : null;
    if (!p || typeof p !== "object") return DEFAULTS;
    return {
      rootPc:
        Number.isInteger(p.rootPc) && p.rootPc >= 0 && p.rootPc <= 11
          ? p.rootPc
          : DEFAULTS.rootPc,
      baseId: BASES.some((b) => b.id === p.baseId) ? p.baseId : DEFAULTS.baseId,
      seventhId:
        p.seventhId === null || SEVENTHS.some((s) => s.id === p.seventhId)
          ? p.seventhId
          : DEFAULTS.seventhId,
      extensions: Array.isArray(p.extensions)
        ? p.extensions.filter((e) => EXTENSIONS.some((x) => x.id === e))
        : DEFAULTS.extensions,
      flats: typeof p.flats === "boolean" ? p.flats : DEFAULTS.flats,
    };
  } catch {
    return DEFAULTS;
  }
};

const saveState = (state) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
};

/* ---------- display labels (contracts don't carry these) ---------- */

const SEVENTH_LABELS = { b7: "♭7", maj7: "maj7", 6: "6" };
const EXTENSION_LABELS = {
  9: "9",
  b9: "♭9",
  "#9": "♯9",
  11: "11",
  "#11": "♯11",
  13: "13",
  b13: "♭13",
};

/* ---------- keyboard range shown for the chord ---------- */

// Fit the display range around the voicing itself — C at-or-below the lowest
// note to C at-or-above the highest, at least two octaves — so the whole
// chord is on screen without scrolling.
const rangeAround = (midis) => {
  if (!midis.length) return { low: 48, high: 72 };
  const lo = Math.min(...midis);
  const hi = Math.max(...midis);
  const low = lo - (lo % 12);
  const high = Math.max(hi + ((12 - (hi % 12)) % 12), low + 24);
  return { low, high };
};

/* ---------- small shared UI ---------- */

const Chip = ({ on, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={on}
    className={cn(
      "rounded-full border px-3 py-1.5 text-sm transition-colors",
      on
        ? "border-fuchsia-400 bg-fuchsia-500/25 text-fuchsia-100"
        : "border-stone-700 bg-stone-800 text-stone-300 hover:border-stone-500"
    )}
  >
    {children}
  </button>
);

const FieldLabel = ({ children }) => (
  <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-stone-500">
    {children}
  </p>
);

const Panel = ({ children, className }) => (
  <div className={cn("rounded-2xl border border-stone-800 bg-stone-900/80 p-4", className)}>
    {children}
  </div>
);

const ACTION_BTN =
  "rounded-full border border-fuchsia-500/40 bg-fuchsia-500/15 px-4 py-2 text-sm font-medium " +
  "text-fuchsia-100 transition-colors hover:bg-fuchsia-500/25";

// Compare two extension-id arrays as sets (order doesn't matter).
const sameExtensionSet = (a = [], b = []) =>
  a.length === b.length && a.every((x) => b.includes(x));

/* ---------- the page ---------- */

export default function ChordCraftPalette() {
  const init = useMemo(() => loadState(), []);

  const [rootPc, setRootPc] = useState(init.rootPc);
  const [baseId, setBaseId] = useState(init.baseId);
  const [seventhId, setSeventhId] = useState(init.seventhId);
  const [extensions, setExtensions] = useState(init.extensions);
  const [useFlats, setUseFlats] = useState(init.flats);

  useEffect(() => {
    saveState({ rootPc, baseId, seventhId, extensions, flats: useFlats });
  }, [rootPc, baseId, seventhId, extensions, useFlats]);

  const noteNames = useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;

  const spec = useMemo(
    () => ({ rootPc, base: baseId, seventh: seventhId, extensions }),
    [rootPc, baseId, seventhId, extensions]
  );
  const symbol = useMemo(() => chordSymbol(spec, useFlats), [spec, useFlats]);

  const toggleExtension = (id) => {
    setExtensions((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));
  };

  /* ----- chord tones -> CraftPiano roles: skeleton 'right', color tones 'color' ----- */

  const extensionPcs = useMemo(() => {
    const set = new Set();
    extensions.forEach((extId) => {
      const ext = EXTENSIONS.find((e) => e.id === extId);
      if (ext) set.add(((rootPc + ext.semis) % 12 + 12) % 12);
    });
    return set;
  }, [extensions, rootPc]);

  const { chordMidis, roles } = useMemo(() => {
    const voicing = voiceChord(spec, 1) || { left: [], right: [] };
    const midis = [...new Set([...(voicing.left || []), ...(voicing.right || [])])].sort(
      (a, b) => a - b
    );
    const r = {};
    midis.forEach((m) => {
      const pc = ((m % 12) + 12) % 12;
      r[m] = extensionPcs.has(pc) ? "color" : "right";
    });
    return { chordMidis: midis, roles: r };
  }, [spec, extensionPcs]);

  const { low: pianoLow, high: pianoHigh } = useMemo(
    () => rangeAround(chordMidis),
    [chordMidis]
  );

  const skeletonMidis = useMemo(
    () => chordMidis.filter((m) => roles[m] !== "color"),
    [chordMidis, roles]
  );

  /* ----- listening: hear it / strum it / plain vs colored ----- */

  const handleHear = useCallback(() => {
    chordMidis.forEach((m) => playNote(PATCHES.softKeys, m, 1400, 0));
  }, [chordMidis]);

  const handleStrum = useCallback(() => {
    chordMidis.forEach((m, i) => playNote(PATCHES.softKeys, m, 1400, i * 40));
  }, [chordMidis]);

  const handlePlainVsColored = useCallback(() => {
    skeletonMidis.forEach((m) => playNote(PATCHES.softKeys, m, 900, 0));
    const colorAt = 900 + 300;
    chordMidis.forEach((m) => playNote(PATCHES.softKeys, m, 1400, colorAt));
  }, [skeletonMidis, chordMidis]);

  /* ----- optional play-along: on-screen keys + MIDI, just for feel ----- */

  const activeRef = useRef(new Set());
  const [activeMidis, setActiveMidis] = useState(() => new Set());

  const press = useCallback((midi) => {
    if (activeRef.current.has(midi)) return;
    activeRef.current.add(midi);
    setActiveMidis(new Set(activeRef.current));
    playNote(PATCHES.softKeys, midi, 900, 0);
  }, []);

  const release = useCallback((midi) => {
    activeRef.current.delete(midi);
    setActiveMidis(new Set(activeRef.current));
  }, []);

  useMidiInput(press, release, true);

  /* ----- PALETTE: matched preset + tappable gallery ----- */

  const matchedPreset = useMemo(
    () =>
      PALETTE.find(
        (p) =>
          p.spec.base === baseId &&
          (p.spec.seventh ?? null) === seventhId &&
          sameExtensionSet(p.spec.extensions || [], extensions)
      ) || null,
    [baseId, seventhId, extensions]
  );

  const applyPreset = useCallback(
    (presetId) => {
      const applied = specFromPreset(presetId, rootPc);
      if (!applied) return;
      setBaseId(applied.base);
      setSeventhId(applied.seventh ?? null);
      setExtensions(applied.extensions || []);
    },
    [rootPc]
  );

  return (
    <CraftShell room="palette" title="Palette" subtitle="Color a chord, hear what changes.">
      {/* min-w-0 on the grid items so the wide piano scrolls inside its panel
          instead of inflating the column (grid items default to
          min-width:auto). */}
      <div className="grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
        {/* ---------- left panel: pickers ---------- */}
        <Panel className="min-w-0 space-y-5 lg:sticky lg:top-4 lg:self-start">
          <div>
            <FieldLabel>Root</FieldLabel>
            <div className="grid grid-cols-4 gap-1.5">
              {noteNames.map((name, pc) => (
                <Chip key={pc} on={rootPc === pc} onClick={() => setRootPc(pc)}>
                  {name}
                </Chip>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-1.5">
              <Chip on={!useFlats} onClick={() => setUseFlats(false)}>
                spell with ♯
              </Chip>
              <Chip on={useFlats} onClick={() => setUseFlats(true)}>
                spell with ♭
              </Chip>
            </div>
            <p className="mt-1.5 text-xs text-stone-600">
              same keys, different names — C♯ and D♭ are the same note.
            </p>
          </div>

          <div>
            <FieldLabel>Base</FieldLabel>
            <div className="flex flex-wrap gap-1.5">
              {BASES.map((b) => (
                <Chip key={b.id} on={baseId === b.id} onClick={() => setBaseId(b.id)}>
                  {b.label}
                </Chip>
              ))}
            </div>
          </div>

          <div>
            <FieldLabel>Seventh</FieldLabel>
            <div className="flex flex-wrap gap-1.5">
              <Chip on={seventhId === null} onClick={() => setSeventhId(null)}>
                none
              </Chip>
              {SEVENTHS.map((s) => (
                <Chip key={s.id} on={seventhId === s.id} onClick={() => setSeventhId(s.id)}>
                  {SEVENTH_LABELS[s.id] || s.id}
                </Chip>
              ))}
            </div>
          </div>

          <div>
            <FieldLabel>Extensions</FieldLabel>
            <div className="flex flex-wrap gap-1.5">
              {EXTENSIONS.map((e) => (
                <Chip
                  key={e.id}
                  on={extensions.includes(e.id)}
                  onClick={() => toggleExtension(e.id)}
                >
                  {EXTENSION_LABELS[e.id] || e.id}
                </Chip>
              ))}
            </div>
            <p className="mt-2 text-xs text-stone-600">
              stack whatever you like — this is a sandbox, not a rulebook.
            </p>
          </div>
        </Panel>

        {/* ---------- main: symbol + piano + listen buttons + gallery ---------- */}
        <div className="min-w-0 space-y-6">
          <Panel className="flex flex-col items-center gap-6 py-8 text-center">
            <div
              className="font-serif text-amber-50"
              style={{ fontSize: "clamp(3rem, 10vw, 7rem)", lineHeight: 1 }}
            >
              {symbol}
            </div>

            <div className="w-full overflow-x-auto">
              <CraftPiano
                low={pianoLow}
                high={pianoHigh}
                roles={roles}
                activeMidis={activeMidis}
                onPress={press}
                onRelease={release}
                useFlats={useFlats}
                showLabels
              />
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              <button type="button" className={ACTION_BTN} onClick={handleHear}>
                hear it
              </button>
              <button type="button" className={ACTION_BTN} onClick={handleStrum}>
                strum it
              </button>
              <button type="button" className={ACTION_BTN} onClick={handlePlainVsColored}>
                plain vs colored
              </button>
            </div>
          </Panel>

          <Panel>
            {matchedPreset ? (
              <>
                <p className="text-xs font-medium uppercase tracking-wider text-fuchsia-400">
                  named color
                </p>
                <h3 className="mt-1 font-serif text-2xl text-amber-50">{matchedPreset.label}</h3>
                <p className="mt-2 text-stone-300">{matchedPreset.mood}</p>
                <p className="mt-1 text-sm text-stone-500">
                  heard in: {matchedPreset.heardIn}
                </p>
              </>
            ) : (
              <p className="text-stone-400">
                Not one of the named colors below — you're mixing your own, which is exactly
                the point.
              </p>
            )}
          </Panel>

          <Panel>
            <FieldLabel>Palette</FieldLabel>
            <div className="grid gap-2 sm:grid-cols-2">
              {PALETTE.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p.id)}
                  className={cn(
                    "rounded-xl border px-3 py-2 text-left transition-colors",
                    matchedPreset && matchedPreset.id === p.id
                      ? "border-fuchsia-400 bg-fuchsia-500/15"
                      : "border-stone-800 bg-stone-900/60 hover:border-stone-600"
                  )}
                >
                  <span className="font-serif text-base text-amber-50">try {p.label}</span>
                  <span className="mt-0.5 block text-xs text-stone-400">{p.mood}</span>
                </button>
              ))}
            </div>
            <p className="mt-4 text-sm italic text-stone-500">
              moods are doors, not fences — any color works in any genre.
            </p>
          </Panel>
        </div>
      </div>
    </CraftShell>
  );
}
